import { createTheme } from '@mui/material/styles';

const themer = async (domainUISettings, clientId) => {
    ((d, l, id) => {
        const fss = d.getElementsByTagName(l)[0];
        if (d.getElementById(id)) return;
        const ss = d.createElement(l);
        ss.id = id;
        ss.rel = 'stylesheet';
        ss.href = domainUISettings.primaryFontUrl;
        fss.parentNode.insertBefore(ss, fss);
    })(document, 'link', 'font-importer-primary');
    ((d, l, id) => {
        const fss = d.getElementsByTagName(l)[0];
        if (d.getElementById(id)) return;
        const ss = d.createElement(l);
        ss.id = id;
        ss.rel = 'stylesheet';
        ss.href = domainUISettings.secondaryFontUrl;
        fss.parentNode.insertBefore(ss, fss);
    })(document, 'link', 'font-importer-secondary');

    if (clientId === 1) {
        return (await import('./defaultTheme')).default;
    }

    const primaryColors = {
        currentPrimary: 'rgb(56, 149, 239)',
        main: domainUISettings?.primaryColor,
        contrastText: domainUISettings?.contrastColor,
    };

    const secondaryColors = {
        main: domainUISettings?.primaryColor,
    };

    const callToAction = {
        main: domainUISettings?.ctaTextColor,
        contrast: domainUISettings?.ctaFillColor,
    };

    const errorColors = {
        light: '#FF908F',
        main: '#FF5D5C',
        dark: '#CC2A29',
        contrastText: '#fff',
    };

    const successColors = {
        light: '#8FFFD3',
        main: '#5AE2A1',
        contrastText: '#fff',
    };

    const backgroundColors = {
        light: '#fff',
        main: '#f2f2f2',
        paper: '#fff',
        default: '#fafafa',
    };

    const textColors = {
        primary: '#333',
        secondary: '#5A5555',
        hint: '#908c88',
        listActive: '#333',
        listInactive: '#333',
    };

    const fonts = {
        headerFont: `"${domainUISettings.primaryFontName}", sans-serif`,
        bodyFont: `"${domainUISettings.secondaryFontName}", sans-serif`,
    };

    const placeholder = {
        opacity: 1,
        color: textColors.hint,
        lineHeight: 1.6,
    };

    const customGlobals = {
        iconFill: {
            light: primaryColors.light,
            main: primaryColors.main,
        },
        cta: {
            variant: 'contained',
            color: 'primary',
            fullWidth: false,
            hexColor: primaryColors.main,
        },
        input: {
            variant: 'outlined',
            color: domainUISettings.inputBorderColor,
        },
        tabs: {
            color: 'secondary',
            align: 'left',
        },
    };

    const theme = createTheme({
        custom: {
            ...customGlobals,
            authView: {
                ...customGlobals,
                nav: {
                    top: 120,
                    leftPercent: '40%',
                    translateX: 'translateX(-60.5%)',
                },
                header: {
                    backgroundColor: domainUISettings?.headerColor,
                },
                headings: {
                    align: 'left',
                    color: textColors.primary,
                },
                joinHeader: {
                    headerText: 'How to Join',
                    justified: 'left',
                },
                signupExtraHeader: {
                    header: 'How to Join',
                },
            },
            appView: {
                ...customGlobals,
                header: {
                    backgroundColor: primaryColors.main,
                    contrastColor: '#333',
                },
                viewHeader: {
                    backgroundColor: '#fff',
                },
                nav: {
                    defaultIconColor: '#333',
                    selectedIconColor: callToAction.main,
                    hoverIconColor: '#fff',
                    hoverBackgroundColor: secondaryColors.main,
                    selectedBackgroundColor: secondaryColors.main,
                },
                messaging: {
                    sidebar: {
                        inactive: {
                            color: textColors.listInactive,
                            background: '#fff',
                        },
                        activeTab: {
                            color: textColors.listActive,
                            background: 'rgba(128, 190, 64, 0.1)',
                        },
                        background: '#fff',
                    },
                    main: {
                        background: '#F2F2F2',
                    },
                    chat: {
                        background: '#fff',
                    },
                },
                settings: {
                    cards: {
                        background: '#fff',
                    },
                },
                dashboard: {
                    headerCard: {
                        background: '#fff',
                    },
                },
                postView: {
                    header: '#fff',
                    footer: '#fff',
                    footerText: primaryColors.main,
                    cards: '#fff',
                    font: '#000',
                    tagColor: secondaryColors.lightAlt,
                    iconColor: secondaryColors.lightAlt,
                    cardFill: '#f5f5f5',
                },
                payments: {
                    tables: {
                        background: '#fff',
                    },
                },
                products: {
                    productDetail: 'tabs',
                },
                dialogs: {
                    cta: {
                        ...customGlobals.cta,
                        variant: 'text',
                        color: primaryColors.main,
                    },
                },
            },
        },
        palette: {
            mode: 'light',
            primary: { ...primaryColors },
            secondary: { ...secondaryColors },
            error: { ...errorColors },
            success: { ...successColors },
            background: { ...backgroundColors },
            text: { ...textColors },
        },
        shape: {
            borderRadius: 0,
        },
        typography: {
            fontFamily: fonts.bodyFont, // Ensure this is a valid font-family string
            fontStyle: 'normal',
            h2: {
                fontFamily: fonts.headerFont, // Ensure this is a valid font-family string
                fontWeight: 700,
                fontSize: '2.3rem',
                textTransform: 'uppercase',
            },
            h3: {
                fontFamily: fonts.headerFont,
                fontWeight: 400,
                fontSize: '1.4rem',
                lineHeight: 2,
                letterSpacing: '2px',
                textTransform: 'uppercase',
            },
            h4: {
                fontFamily: fonts.headerFont,
                fontWeight: 400,
                fontSize: '1.6rem',
            },
            h5: {
                fontFamily: fonts.headerFont,
                fontSize: '1.75rem',
                textTransform: 'uppercase',
            },
            h6: {
                fontFamily: fonts.headerFont,
                fontWeight: 700,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
            },
            subtitle1: {
                fontSize: '1rem',
                lineHeight: 1.28,
                color: '#9B9B9B',
            },
            subtitle2: {
                fontFamily: fonts.headerFont,
                lineHeight: 1.2,
            },
            body1: {
                lineHeight: 1.2,
            },
            body2: {
                lineHeight: 1.33,
            },
            button: {
                fontFamily: fonts.headerFont,
                fontWeight: 900,
                fontSize: '1.125rem', // Ensure fontSize is a valid CSS size value
                letterSpacing: '1px',
                textTransform: 'uppercase',
            },
        },
        // need to have at least 2 entries in shadows list so that we don't cause errors. Current Theme (defaultTheme) uses elevation 0 and 1
        shadows: [
            'none',
            'none',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
            '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)',
        ],
        components: {
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 6,
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontWeight: 700,
                        fontSize: '1.25rem',
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        minHeight: 30,
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: '#333',
                        bottom: 0,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        marginLeft: 50,
                        fontWeight: 700,
                        letterSpacing: 0,
                        '@media (min-width:960px)': {
                            minWidth: 30,
                        },
                        '@media (min-width:600px)': {
                            padding: '6px 0px 0px 0px',
                            fontSize: 14,
                            minHeight: 30,
                            minWidth: 30,
                            margin: '0 25px',
                        },
                    },
                    textColorPrimary: {
                        color: '#333',
                        opacity: 1,
                        fontWeight: 700,
                    },
                    textColorInherit: {
                        color: 'inherit',
                        opacity: 1,
                        fontWeight: 700,
                        background: 'transparent',
                        '&.Mui-selected': {
                            opacity: 1,
                        },
                        '&.Mui-disabled': {
                            opacity: 0.4,
                        },
                        '&:hover': {
                            opacity: 1,
                        },
                    },
                },
            },
            MuiPickersClockNumber: {
                styleOverrides: {
                    clockNumberSelected: {
                        color: '#fff',
                    },
                },
            },
            MuiPickersToolbarText: {
                styleOverrides: {
                    toolbarTxt: {
                        color: '#fff',
                    },
                    toolbarBtnSelected: {
                        color: '#fff',
                        fontWeight: 700,
                    },
                },
            },
            MuiPickersToolbarButton: {
                styleOverrides: {
                    toolbarBtn: {
                        color: '#fff',
                    },
                    toolbarBtnSelected: {
                        fontWeight: 700,
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        color: '#333',
                    },
                    outlined: {
                        fontWeight: 400,
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        border: `1px solid ${'#333'}`,
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        border: `1px solid ${'#333'}`,
                    },
                },
            },
            MuiCardActions: {
                styleOverrides: {
                    root: {
                        backgroundColor: primaryColors.main,
                        color: secondaryColors.contrastText,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        height: 40,
                        '&.Mui-disabled': {
                            color: '#E7E7DD',
                        },
                    },
                    containedPrimary: {
                        color: primaryColors.contrastText,
                        boxShadow: 'none',
                        padding: '6px 30px',
                        borderRadius: domainUISettings.ctaBorderRadius,
                        fontWeight: 800,
                        backgroundColor: primaryColors.main,
                    },
                    outlinedPrimary: {
                        padding: '9px 50px',
                        borderRadius: 10,
                        borderWidth: 2,
                        '&:hover': {
                            borderWidth: 2,
                        },
                        '&.Mui-disabled': {
                            color: '#999',
                            borderColor: '#999',
                            borderWidth: 2,
                        },
                        '& > .MuiButton-label': {
                            height: '-webkit-fill-available',
                        },
                    },
                    outlinedSecondary: {
                        height: 40,
                    },
                    outlined: {
                        height: 40,
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#fff',
                        borderRadius: domainUISettings.inputBorderRadius,
                        height: 34,
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: domainUISettings.inputBorderColor,
                            borderWidth: 1,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: domainUISettings.inputBorderColor,
                            borderWidth: 1,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: domainUISettings.inputBorderColor,
                            borderWidth: 1,
                        },
                    },
                    adornedEnd: {
                        backgroundColor: primaryColors.main,
                        paddingRight: 0,
                        '& svg': {
                            color: primaryColors.contrastText,
                        },
                    },
                },
            },
            MuiFilledInput: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#fff',
                        '&:hover': {
                            backgroundColor: '#fff',
                        },
                        '&.Mui-focused': {
                            backgroundColor: '#fff',
                        },
                        border: `1px solid ${'#333'}`,
                        height: 42,
                    },
                    input: {
                        padding: 10,
                        backgroundColor: '#fff',
                        height: 42,
                    },
                    adornedEnd: {
                        paddingRight: 0,
                    },
                    underline: {
                        '&:after': {
                            borderBottom: 'none',
                        },
                        '&.Mui-focused:after': {
                            transform: 'scaleX(1)',
                        },
                        '&.Mui-error:after': {},
                        '&:before': {
                            borderBottom: 'none',
                        },
                        '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error):before': {
                            borderBottom: 'transparent',
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: 'none',
                        },
                        '&.Mui-disabled:before': {
                            borderBottom: 'none',
                            borderBottomStyle: 'unset',
                        },
                    },
                    inputMultiline: {
                        height: 'auto',
                        padding: 10,
                    },
                    multiline: {
                        padding: 0,
                        position: 'absolute',
                        zIndex: 100,
                        height: 'auto',
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        overflow: 'hidden',
                        '& .MuiInputBase-input': {
                            height: 25,
                        },
                    },
                    input: {
                        background: '#fff',
                        width: '100%',
                        padding: 10,
                        fontSize: '1rem',
                        '&::placeholder': placeholder,
                        '&::-webkit-input-placeholder': placeholder,
                        '&::-moz-placeholder': placeholder,
                        '&:-ms-input-placeholder': placeholder,
                        '&::-ms-input-placeholder': placeholder,
                        '&:focus': {
                            outline: 0,
                        },
                        fontWeight: 400,
                        'label[data-shrink=false] + $formControl &': {
                            '&:focus::-webkit-input-placeholder': placeholder,
                            '&:focus::-moz-placeholder': placeholder,
                            '&:focus:-ms-input-placeholder': placeholder,
                            '&:focus::-ms-input-placeholder': placeholder,
                        },
                    },
                },
            },
            MuiPickersBasePicker: {
                styleOverrides: {
                    container: {
                        border: `1px solid ${'#333'}`,
                    },
                },
            },
            MuiPickersCalendar: {
                styleOverrides: {
                    transitionContainer: {
                        boxSizing: 'border-box',
                    },
                },
            },
            MuiPickersDay: {
                styleOverrides: {
                    day: {
                        '&.Mui-selected': {
                            color: 'white',
                        },
                    },
                },
            },
            PrivateNotchedOutline: {
                styleOverrides: {
                    legend: {
                        lineHeight: '12px',
                    },
                },
            },
        },
    });
    return theme;
};

export default themer;
